import { useState, useEffect } from 'react';
import axios from 'axios';
import { Flame, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CALENDAR_DROPDOWN, CALENDAR_BW_PATH } from '../components/navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://daily-obx-cdn.b-cdn.net/newsletter-images/B.B%20Wicks%20-logo.png";

// NOTE: this dropdown's item order (and the absence of an "active" item,
// even though Wicks is the current page) diverges from the shared
// SHOP_DROPDOWN helper's order — preserved exactly as it was in the
// original inline markup rather than normalized.
const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  {
    label: 'Shop',
    dropdown: [
      { to: '/gallery', label: 'Beach Bum Gallery' },
      { to: '/shop/muggs', label: 'B.B. Muggs' },
      { to: '/shop/tees', label: 'Beach Bum Tees' },
      { to: '/shop/notecards', label: 'Mostly Good Notecards' },
      { to: '/shop/mrsbum', label: 'Mrs. Bum' },
      { to: '/shop/wicks', label: 'B.B. Wicks' },
      { to: '/shop/calendar', label: 'Calendar' },
      { to: CALENDAR_BW_PATH, label: 'B&W Calendar' },
    ],
  },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About' },
];

const FOOTER_LEGAL_LINKS = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
  { to: '/shipping', label: 'Shipping' },
  { to: '/faq', label: 'FAQ' },
];

function Wicks() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  const { addItem, itemCount, openCart } = useCart();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/wicks-products`);
      setProducts(response.data);
      const initQuantities = {};
      response.data.forEach(p => { initQuantities[p.id] = 1; });
      setQuantities(initQuantities);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (productId, delta, maxStock) => {
    setQuantities(prev => {
      const next = (prev[productId] || 1) + delta;
      const capped = maxStock != null ? Math.min(next, maxStock) : next;
      return { ...prev, [productId]: Math.max(1, capped) };
    });
  };

  const handleAddToCart = (product) => {
    addItem({
      shop: 'wicks',
      product_id: product.id,
      product_title: product.title,
      image_url: product.image_url,
      variant: null,
      variant_label: null,
      price: product.price,
      quantity: quantities[product.id] || 1,
      max_quantity: product.stock,
      special_instructions: null
    });
    toast.success(`${product.title} added to cart!`);
  };

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} cart={{ itemCount, onClick: openCart }} />

      {/* Hero Section */}
      <section className="wicks-hero">
        <div className="hero-content">
          <img
            src={LOGO}
            alt="B.B. Wicks - Old Fashioned Candle Maker"
            className="shop-hero-logo"
            style={{ maxWidth: '350px', marginBottom: '20px' }}
          />
          <p className="hero-description">
            Hand-poured candles crafted with care in the Outer Banks
          </p>
        </div>
      </section>

      {/* Products */}
      <main className="wicks-main">
        {loading ? (
          <div className="loading-screen">
            <div className="loading-spinner"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state" style={{ padding: '80px 20px', textAlign: 'center' }}>
            <Flame size={64} strokeWidth={1} style={{ color: '#ccc', marginBottom: '20px' }} />
            <h2 style={{ marginBottom: '10px' }}>Candles Coming Soon</h2>
            <p style={{ color: '#666' }}>We're hand-pouring new creations. Check back soon!</p>
          </div>
        ) : (
          <div className="products-grid">
            {products.map(product => {
              const outOfStock = product.stock === 0;
              return (
                <div key={product.id} className="wicks-product-card">
                  <div
                    className="product-image-container"
                    style={{ position: 'relative' }}
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    <img
                      src={product.image_url}
                      alt={product.title}
                      className="product-image"
                      draggable="false"
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                    />
                    {outOfStock && (
                      <div style={{ position: 'absolute', top: '10px', left: '10px', background: '#dc2626', color: '#fff', padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>
                        Out of Stock
                      </div>
                    )}
                  </div>
                  <div className="product-details">
                    <h2 className="product-title">{product.title}</h2>
                    {product.description && (
                      <p className="product-description">{product.description}</p>
                    )}
                    {product.scent && (
                      <p className="product-scent"><strong>Scent:</strong> {product.scent}</p>
                    )}
                    {product.burn_time && (
                      <p className="product-burn-time"><strong>Burn Time:</strong> {product.burn_time}</p>
                    )}

                    <div className="product-price">${product.price.toFixed(2)}</div>

                    {/* Quantity Selector */}
                    <div className="quantity-selector">
                      <label>Quantity:</label>
                      <div className="quantity-controls">
                        <button
                          onClick={() => handleQuantityChange(product.id, -1, product.stock)}
                          disabled={quantities[product.id] <= 1}
                        >
                          -
                        </button>
                        <span>{quantities[product.id] || 1}</span>
                        <button
                          onClick={() => handleQuantityChange(product.id, 1, product.stock)}
                          disabled={product.stock != null && (quantities[product.id] || 1) >= product.stock}
                        >
                          +
                        </button>
                      </div>
                      {product.stock != null && product.stock > 0 && product.stock <= 5 && (
                        <span style={{ fontSize: '13px', color: '#b45309' }}>Only {product.stock} left</span>
                      )}
                    </div>

                    <button
                      className="buy-now-btn"
                      onClick={() => handleAddToCart(product)}
                      disabled={outOfStock}
                    >
                      {outOfStock ? 'Out of Stock' : (
                        <><ShoppingCart size={18} /> Add to Cart</>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="shipping-info">
          <p>Free shipping on orders over $75 • Hand-poured in the Outer Banks</p>
        </div>
      </main>

      {/* Footer */}
      <Footer showLogo={false} legalLinks={FOOTER_LEGAL_LINKS} />
    </div>
  );
}

export default Wicks;
